'use client';

import type { Dispatch, SetStateAction } from 'react';
import { createContext, useMemo, useState, ReactNode, useEffect, useCallback } from 'react';
import type { BrandAndChainResponse } from '@/app/api/venue-autocomplete/route';
import type { AccountTagsOptions } from '@/utils/filters/options/getAccountTagsOptions';
import type { GeoPoint } from '@/models/GeoPoint';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import type { SearchParams } from '@/models/SearchParams';
import type { QueryParserResult } from '@/fetchers/getQueryParserResult';
import type { ShoppingCartItem } from '@/models/ShoppingCartItem';

export type FullMapContextType = {
  mapVenuesForFullMap: CsnSearchVenue[];
  mapVenuesWithAds: CsnSearchVenue[];
  mapSearchParams: SearchParams;
  mapSearchCoordinates: GeoPoint;
  mapGeoLocationData: QueryParserResult;
  mapShoppingCartItems: Map<string, ShoppingCartItem>;
  mapPreviousGeoData: QueryParserResult | null;
  brandCityCountry: BrandAndChainResponse | null;
  accountTagOptions: AccountTagsOptions;
  applicableFiltersInState: string[];
  isListViewLoading: boolean;
  setIsListViewLoading: Dispatch<SetStateAction<boolean>>;
  setMapVenuesForFullMap: Dispatch<SetStateAction<CsnSearchVenue[]>>;
  setMapVenuesWithAds: Dispatch<SetStateAction<CsnSearchVenue[]>>;
  setMapSearchParams: Dispatch<SetStateAction<SearchParams>>;
  setMapSearchCoordinates: Dispatch<SetStateAction<GeoPoint>>;
  setMapGeoLocationData: Dispatch<SetStateAction<QueryParserResult>>;
  setMapShoppingCartItems: Dispatch<SetStateAction<Map<string, ShoppingCartItem>>>;
  setMapPreviousGeoData: Dispatch<SetStateAction<QueryParserResult | null>>;
  setBrandCityCountry: Dispatch<SetStateAction<BrandAndChainResponse | null>>;
  setAccountTagOptions: Dispatch<SetStateAction<AccountTagsOptions>>;
  setApplicableFiltersInState: Dispatch<SetStateAction<string[]>>;
};

export type FullMapProviderProps = {
  children: ReactNode;
  venuesForFullMap: CsnSearchVenue[];
  venuesWithAds: CsnSearchVenue[];
  searchParams: SearchParams;
  searchCoordinates: GeoPoint;
  geoLocationData: QueryParserResult;
  items: Map<string, ShoppingCartItem>;
  previousGeoData: QueryParserResult | null;
  accountTags: AccountTagsOptions;
  applicableFilters: string[];
};

export const FullMapContext = createContext({} as FullMapContextType);

// Note: Fast responder feature needs to be disabled for North American countries
function includeTopResponderFilter(mapGeoLocationData: QueryParserResult, backendApplicableFilters: string[]) {
  const { countryCode } = mapGeoLocationData;
  const isNorthAmericanCountry = countryCode === 'US' || countryCode === 'CA';
  const updatedFilters = [...backendApplicableFilters, ...(!isNorthAmericanCountry ? ['FAST_RESPONDER'] : [])];
  return updatedFilters;
}

export default function FullMapProvider({
  children,
  venuesForFullMap,
  venuesWithAds,
  searchParams,
  searchCoordinates,
  geoLocationData,
  previousGeoData,
  items,
  accountTags,
  applicableFilters
}: FullMapProviderProps) {
  const [isListViewLoading, setIsListViewLoading] = useState(true);
  const [mapVenuesForFullMap, setMapVenuesForFullMap] = useState<CsnSearchVenue[]>(venuesForFullMap);
  const [mapVenuesWithAds, setMapVenuesWithAds] = useState<CsnSearchVenue[]>(venuesWithAds);
  const [mapSearchParams, setMapSearchParams] = useState<SearchParams>(searchParams);
  const [mapSearchCoordinates, setMapSearchCoordinates] = useState<GeoPoint>(searchCoordinates);
  const [mapGeoLocationData, setMapGeoLocationData] = useState<QueryParserResult>(geoLocationData);
  const [mapShoppingCartItems, setMapShoppingCartItems] = useState<Map<string, ShoppingCartItem>>(items);
  const [mapPreviousGeoData, setMapPreviousGeoData] = useState<QueryParserResult | null>(previousGeoData);
  const [brandCityCountry, setBrandCityCountry] = useState<BrandAndChainResponse | null>(null);
  const [accountTagOptions, setAccountTagOptions] = useState<AccountTagsOptions>(accountTags);
  const [applicableFiltersInState, setApplicableFilters] = useState<string[]>(
    includeTopResponderFilter(geoLocationData, applicableFilters)
  );

  const setApplicableFiltersInState = useCallback(
    (backendApplicableFilters: string[]) => {
      const updatedFilters = includeTopResponderFilter(mapGeoLocationData, backendApplicableFilters);
      setApplicableFilters(updatedFilters);
    },
    [mapGeoLocationData]
  );

  useEffect(() => {
    setIsListViewLoading(false);
    setMapVenuesWithAds(venuesWithAds);
  }, [venuesWithAds]);

  useEffect(() => {
    setMapVenuesForFullMap(venuesForFullMap);
  }, [venuesForFullMap]);

  useEffect(() => {
    setMapSearchParams(searchParams);
  }, [searchParams]);

  useEffect(() => {
    setMapSearchCoordinates(searchCoordinates);
  }, [searchCoordinates]);

  useEffect(() => {
    setMapGeoLocationData(geoLocationData);
  }, [geoLocationData]);

  useEffect(() => {
    setMapPreviousGeoData(previousGeoData);
  }, [previousGeoData]);

  useEffect(() => {
    setMapShoppingCartItems(items);
  }, [items]);

  useEffect(() => {
    setAccountTagOptions(accountTags);
  }, [accountTags]);

  useEffect(() => {
    setApplicableFiltersInState(applicableFilters);
  }, [applicableFilters, setApplicableFiltersInState]);

  const value = useMemo(
    () => ({
      mapVenuesForFullMap,
      mapVenuesWithAds,
      mapSearchParams,
      mapSearchCoordinates,
      mapGeoLocationData,
      mapPreviousGeoData,
      mapShoppingCartItems,
      brandCityCountry,
      accountTagOptions,
      applicableFiltersInState,
      isListViewLoading,
      setMapVenuesForFullMap,
      setMapVenuesWithAds,
      setMapSearchParams,
      setMapSearchCoordinates,
      setMapGeoLocationData,
      setMapPreviousGeoData,
      setMapShoppingCartItems,
      setBrandCityCountry,
      setAccountTagOptions,
      setApplicableFiltersInState,
      setIsListViewLoading
    }),
    [
      accountTagOptions,
      applicableFiltersInState,
      brandCityCountry,
      isListViewLoading,
      mapGeoLocationData,
      mapPreviousGeoData,
      mapSearchCoordinates,
      mapSearchParams,
      mapShoppingCartItems,
      mapVenuesForFullMap,
      mapVenuesWithAds,
      setApplicableFiltersInState
    ]
  );
  return <FullMapContext.Provider value={value}>{children}</FullMapContext.Provider>;
}
